import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useShippings, useAllAccounts } from '../hooks';
import { shippingKeys } from '../hooks/queryKeys';
import { ShippingsFilters as ShippingsFiltersBar } from '../components/ShippingsFilters';
import { ShippingsTable, COLUMNS } from '../components/ShippingsTable';
import type { ShippingsFilters as ShippingsFiltersType, ShippingStatus } from '../types';

// Default statuses to show (verified, shipped, received)
const DEFAULT_STATUSES: ShippingStatus[] = ['verified', 'shipped', 'received'];

// localStorage keys
const FILTERS_STORAGE_KEY = 'atek_shippings_filters';
const COLUMN_FILTERS_STORAGE_KEY = 'atek-shippings-column-filters';

// =============================================================================
// Filter Persistence
// =============================================================================

interface PersistedFilters {
  search: string;
  selectedStatuses: ShippingStatus[];
  shippingCompany: string;
  pageSize: number;
  sortKey: ShippingsFiltersType['sortKey'];
  sortDirection: 1 | -1;
}

function loadPersistedFilters(): PersistedFilters {
  try {
    const stored = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        search: parsed.search ?? '',
        selectedStatuses: parsed.selectedStatuses ?? DEFAULT_STATUSES,
        shippingCompany: parsed.shippingCompany ?? 'all',
        pageSize: parsed.pageSize ?? 25,
        sortKey: parsed.sortKey ?? 'createdAt',
        sortDirection: parsed.sortDirection ?? -1,
      };
    }
  } catch {
    // Ignore parse errors
  }
  return {
    search: '',
    selectedStatuses: DEFAULT_STATUSES,
    shippingCompany: 'all',
    pageSize: 25,
    sortKey: 'createdAt',
    sortDirection: -1,
  };
}

function savePersistedFilters(filters: PersistedFilters): void {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // Ignore storage errors
  }
}

// =============================================================================
// Component
// =============================================================================

export default function ShippingsPage() {
  const { t } = useTranslation('shippings');
  const queryClient = useQueryClient();

  // Load persisted state
  const [persistedFilters] = useState(loadPersistedFilters);

  // Server-side filter state (initialized from localStorage)
  const [search, setSearch] = useState(persistedFilters.search);
  const [debouncedSearch, setDebouncedSearch] = useState(persistedFilters.search);
  const [selectedStatuses, setSelectedStatuses] = useState<ShippingStatus[]>(persistedFilters.selectedStatuses);
  const [shippingCompany, setShippingCompany] = useState(persistedFilters.shippingCompany);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(persistedFilters.pageSize);
  const [sortKey, setSortKey] = useState<ShippingsFiltersType['sortKey']>(persistedFilters.sortKey);
  const [sortDirection, setSortDirection] = useState<1 | -1>(persistedFilters.sortDirection);

  // Column filters (client-side refinement, persisted to localStorage)
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(() => {
    try {
      return JSON.parse(localStorage.getItem(COLUMN_FILTERS_STORAGE_KEY) || '{}');
    } catch {
      return {};
    }
  });

  useEffect(() => {
    localStorage.setItem(COLUMN_FILTERS_STORAGE_KEY, JSON.stringify(columnFilters));
  }, [columnFilters]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Debounce toolbar search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Promote text column filter values to server-side search
  const columnSearchText = useMemo(() => {
    return Object.entries(columnFilters)
      .filter(([, value]) => {
        if (!value) return false;
        if (value.includes('>>')) return false; // date-range
        if (value.startsWith('[')) return false; // multi-select
        return true;
      })
      .map(([, value]) => value)
      .join(' ');
  }, [columnFilters]);

  const [debouncedColumnSearch, setDebouncedColumnSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedColumnSearch(columnSearchText);
      if (columnSearchText) setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [columnSearchText]);

  // Combined search: toolbar search + column text filters
  const combinedSearch =
    [debouncedSearch, debouncedColumnSearch].filter(Boolean).join(' ') || undefined;

  // Persist toolbar filters when they change (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      savePersistedFilters({
        search,
        selectedStatuses,
        shippingCompany,
        pageSize,
        sortKey: sortKey!,
        sortDirection,
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [search, selectedStatuses, shippingCompany, pageSize, sortKey, sortDirection]);

  // Build filters object for API call
  const filters: ShippingsFiltersType = {
    search: combinedSearch,
    status_in: selectedStatuses.length > 0 ? selectedStatuses : undefined,
    shippingCompany: shippingCompany !== 'all' ? shippingCompany : undefined,
    sortKey,
    sortDirection,
    pageNumber: page,
    pageLength: pageSize,
  };

  // Query
  const shippingsQuery = useShippings(filters);
  const rawShippings = shippingsQuery.data?.packingSlips || [];
  const total = shippingsQuery.data?.total || 0;

  // Account options for searchable dropdown
  const accountsQuery = useAllAccounts();
  const accountOptions = useMemo(() => {
    const accounts = accountsQuery.data || [];
    const seen = new Set<string>();
    return accounts.reduce<{ label: string; value: string }[]>((acc, a) => {
      if (!seen.has(a.name)) {
        seen.add(a.name);
        acc.push({ label: a.name, value: a.name });
      }
      return acc;
    }, []);
  }, [accountsQuery.data]);

  const searchableSelectOptions = useMemo(
    () => ({ accountName: accountOptions }),
    [accountOptions],
  );

  // Client-side column filtering (date-range and multi-select within page)
  const filteredShippings = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
    if (activeFilters.length === 0) return rawShippings;

    return rawShippings.filter((shipping) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = COLUMNS.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        const rawValue = col.getValue(shipping);

        // Date range: "from>>to"
        if (col.filterType === 'date-range') {
          const [from, to] = filterValue.split('>>');
          const dateStr = String(rawValue || '');
          if (from && dateStr < from) return false;
          if (to && dateStr > to + 'T23:59:59') return false;
          return true;
        }

        // Multi-select / searchable-select: JSON array
        if (col.filterType === 'select' || col.filterType === 'searchable-select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return selected.length === 0 || selected.includes(String(rawValue || ''));
          } catch {
            return true;
          }
        }

        // Text: case-insensitive includes (also promoted to server, kept for accuracy)
        return String(rawValue ?? '')
          .toLowerCase()
          .includes(filterValue.toLowerCase());
      }),
    );
  }, [rawShippings, columnFilters]);

  // Check if any toolbar filters are active (not default)
  const hasActiveFilters =
    search !== '' ||
    JSON.stringify(selectedStatuses) !== JSON.stringify(DEFAULT_STATUSES) ||
    shippingCompany !== 'all';

  // Handlers
  const handleSort = (key: ShippingsFiltersType['sortKey']) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortKey(key);
      setSortDirection(-1);
    }
    setPage(0);
  };

  const handleStatusChange = (statuses: ShippingStatus[]) => {
    setSelectedStatuses(statuses);
    setPage(0);
  };

  const handleCompanyChange = (value: string) => {
    setShippingCompany(value);
    setPage(0);
  };

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    setPage(0);
  };

  const handleClearAllFilters = () => {
    setSearch('');
    setSelectedStatuses(DEFAULT_STATUSES);
    setShippingCompany('all');
    setPage(0);
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: shippingKeys.all });
  };

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">
            {t('page.title', 'Shippings')}{' '}
            <small>{t('page.subtitle', 'Track packing slips and deliveries')}</small>
          </h1>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={shippingsQuery.isFetching}
        >
          <RefreshCw
            className={`h-4 w-4 mr-1 ${shippingsQuery.isFetching ? 'animate-spin' : ''}`}
          />
          {t('toolbar.refresh', 'Refresh')}
        </Button>
      </div>

      {/* Server-side Filters */}
      <div className="flex items-center justify-between gap-4">
        <ShippingsFiltersBar
          search={search}
          onSearchChange={setSearch}
          selectedStatuses={selectedStatuses}
          onStatusChange={handleStatusChange}
          shippingCompany={shippingCompany}
          onCompanyChange={handleCompanyChange}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          onClearAll={handleClearAllFilters}
          hasActiveFilters={hasActiveFilters}
        />
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {total} {t('toolbar.count', 'shipping')}
          {total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      {shippingsQuery.error ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-red-500">
              {t('error.loading', 'Error loading shippings. Please try again.')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <ShippingsTable
          shippings={filteredShippings}
          pageItemCount={rawShippings.length}
          total={total}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          columnFilters={columnFilters}
          onColumnFilterChange={setColumnFilter}
          isLoading={shippingsQuery.isLoading}
          searchableSelectOptions={searchableSelectOptions}
        />
      )}
    </div>
  );
}
